// src/pages/Community/GeneralDiscussion.jsx
import React, { useState, useEffect, useRef } from "react";
import "./css/GeneralDiscussion.css";
import { db } from "../../firebase";
import {
  collection,
  getDocs,
  query,
  orderBy,
  addDoc,
  doc,
  updateDoc,
  getDoc,
  arrayUnion,
  arrayRemove,
  serverTimestamp,
  onSnapshot,
  limit,
  startAfter,
  runTransaction,
  increment,
  setDoc,
} from "firebase/firestore";
import PageWrapper from "../../components/PageWrapper";
import CreatePostModal from "./CreatePostModal";
import ReactMarkdown from "react-markdown";
import FullscreenImageModal from "./FullscreenImageModal";
import { toast } from "react-toastify";
import { logFirebaseError, isPermissionDenied } from "../../utils/fbError";
import { enqueueLikeGive, enqueueLikeUndo } from "../../services/xpService";
// ---- Safe timestamp → Date helper (prevents "Invalid Date") ----
const toDateSafe = (ts) => {
  if (!ts) return null;

  // Firestore Timestamp
  if (typeof ts?.toDate === "function") return ts.toDate();

  // Firestore plain object { seconds, nanoseconds }
  if (typeof ts === "object" && typeof ts.seconds === "number") {
    const ms = ts.seconds * 1000 + Math.floor((ts.nanoseconds || 0) / 1e6);
    return new Date(ms);
  }

  // ISO string or epoch millis
  const d = new Date(ts);
  return Number.isNaN(d.getTime()) ? null : d;
};

// ---------- Debug helpers ----------
const DEBUG_LIKES = true;

function dbgLike(label, data = {}) {
  if (!DEBUG_LIKES) return;
  try {
    const time = new Date().toISOString();
    console.groupCollapsed(
      `%c[Like] ${label} @ ${time}`,
      "color:#a64ef5;font-weight:600"
    );
    if (data && typeof data === "object") {
      const entries = Object.entries(data);
      if (entries.length) {
        // Prefer table for flat-ish objects
        const isFlat = entries.every(
          ([_, v]) => v === null || typeof v !== "object"
        );
        isFlat ? console.table(data) : console.log(data);
      }
    }
    console.groupEnd();
  } catch (e) {
    // never throw from logging
  }
}

function dbgError(context, err, extra = {}) {
  try {
    console.group(
      `%c[Like][ERROR] ${context}`,
      "color:#ff3b30;font-weight:700"
    );
    console.error(err);
    if (extra && Object.keys(extra).length) {
      console.log("extra:", extra);
    }
    // If it looks like a FirebaseError
    const code = err?.code || err?.error?.code;
    const message = err?.message || err?.error?.message;
    if (code || message) {
      console.warn("FirebaseError:", { code, message });
    }
    console.groupEnd();
  } catch (_) {}
}

const GeneralDiscussion = ({ user, onClose, onPostCreated }) => {
  const [posts, setPosts] = useState([]);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [userProfiles, setUserProfiles] = useState({});
  const [profiles, setProfiles] = useState({});
  const [fullscreenImage, setFullscreenImage] = useState(null);
  const [commentDrafts, setCommentDrafts] = useState({});
  const [commentSending, setCommentSending] = useState({}); // { [postId]: boolean }
  const [pollSubmitting, setPollSubmitting] = useState({}); // { [postId]: boolean }

  // simple per-post in-flight lock to prevent double queueing intents
  const likeLocks = useRef(new Set());

  // Realtime posts (newest → oldest) with safe sorting
  useEffect(() => {
    const qy = query(
      collection(db, "community_posts"),
      orderBy("timestamp", "desc")
    );

    const unsub = onSnapshot(
      qy,
      (snapshot) => {
        const list = snapshot.docs.map((d) => {
          const data = d.data();
          return { id: d.id, ...data, commentCount: data.commentCount ?? 0 };
        });

        // Fallback sort in case some docs have string/object timestamps
        list.sort((a, b) => {
          const at = toDateSafe(a.timestamp)?.getTime() || 0;
          const bt = toDateSafe(b.timestamp)?.getTime() || 0;
          return bt - at;
        });

        setPosts(list);
      },
      (err) => {
        logFirebaseError("community_posts.onSnapshot", err, { filter });
        if (isPermissionDenied(err)) {
          toast.error("You don’t have permission to read posts.");
        } else {
          toast.error("Couldn’t load posts in real time.");
        }
      }
    );

    return () => unsub();
  }, []);

  // Load public profiles
  useEffect(() => {
    const fetchProfiles = async () => {
      const snapshot = await getDocs(collection(db, "public_profiles"));
      const map = {};
      snapshot.docs.forEach((d) => (map[d.id] = d.data()));
      setUserProfiles(map);
      setProfiles(map);
    };
    fetchProfiles();
  }, []);

  useEffect(() => {
    if (!user && filter === "mine") setFilter("all");
  }, [user, filter]);

  // If CreatePostModal also writes to Firestore, the snapshot will bring it in.
  // Keep this as a dedupe-safe optimistic prepend if you still call it.
  const handlePostCreated = (newPost) =>
    setPosts((prev) =>
      prev.some((p) => p.id === newPost.id) ? prev : [newPost, ...prev]
    );

  const filteredPosts = posts.filter((post) => {
    if (filter === "mine") {
      if (!user) return false;
      return post.userId === user.uid;
    }
    if (filter !== "all" && post.type !== filter) return false;
    if (search && !post.content.toLowerCase().includes(search.toLowerCase()))
      return false;
    return true;
  });

  const handleLikePost = async (postId) => {
    if (!user) {
      toast.info("Please log in to like posts.");
      return;
    }
    if (likeLocks.current.has(postId)) {
      dbgLike("skip: lock busy", { postId });
      return;
    }
    likeLocks.current.add(postId);

    const postRef = doc(db, "community_posts", postId);
    dbgLike("START like toggle", {
      postId,
      userUid: user.uid,
      path: postRef.path,
    });

    try {
      const snap = await getDoc(postRef);
      if (!snap.exists()) {
        dbgLike("abort: post missing", { postId });
        return;
      }

      const data = snap.data() || {};
      const likes = Array.isArray(data.likes) ? data.likes : [];
      const alreadyLiked = likes.includes(user.uid);

      dbgLike("current state", {
        postId,
        ownerUid: data.userId,
        likesCount: likes.length,
        alreadyLiked,
        likesPreview: likes.slice(0, 10),
      });

      // Attempt the write
      const op = alreadyLiked ? "arrayRemove" : "arrayUnion";
      dbgLike("write attempt", { postId, op, userUid: user.uid });

      await updateDoc(postRef, {
        likes: alreadyLiked ? arrayRemove(user.uid) : arrayUnion(user.uid),
      });

      dbgLike("write success", { postId, op, userUid: user.uid });

      // XP queue (non-blocking, with console feedback)
      const postOwnerUid = data.userId;
      if (!alreadyLiked) {
        dbgLike("XP grant enqueue", {
          type: "LIKE_GIVE",
          giverUid: user.uid,
          postOwnerUid,
          postId,
        });
        Promise.resolve(
               enqueueLikeGive({
           giverUid: user.uid,
           postId,
           postOwnerUid,
           postCollection: "community_posts",
           likeModel: "array",
         })
        )
          .then(() =>
            dbgLike("XP grant enqueued OK", { type: "LIKE_GIVE", postId })
          )
          .catch((e) =>
            dbgError("enqueueLikeGive failed", e, {
              postId,
              giverUid: user.uid,
              postOwnerUid,
            })
          );
      } else {
        dbgLike("XP undo enqueue", {
          type: "LIKE_UNDO",
          giverUid: user.uid,
          postOwnerUid,
          postId,
        });
        Promise.resolve(
           enqueueLikeUndo({
           giverUid: user.uid,
           postId,
           postOwnerUid,
           postCollection: "community_posts",
           likeModel: "array",
         })
        )
          .then(() =>
            dbgLike("XP undo enqueued OK", { type: "LIKE_UNDO", postId })
          )
          .catch((e) =>
            dbgError("enqueueLikeUndo failed", e, {
              postId,
              giverUid: user.uid,
              postOwnerUid,
            })
          );
      }

      // Optimistic UI (hardened)
      setPosts((prev) =>
          prev.map((p) => {
            if (p.id !== postId) return p;

            const prevLikes = Array.isArray(p.likes) ? p.likes : [];
            const had = prevLikes.includes(user.uid);

            let nextLikes;
            if (alreadyLiked) {
              // Intention: unlike. Only remove if it's there (snapshot might have already removed).
              nextLikes = had ? prevLikes.filter((id) => id !== user.uid) : prevLikes;
            } else {
              // Intention: like. Ensure uniqueness (snapshot might have already added it).
              nextLikes = Array.from(new Set([...prevLikes, user.uid]));
            }

            return {
              ...p,
              likes: nextLikes,
              userHasLiked: !alreadyLiked,
              likeCount: nextLikes.length, // keep in sync with the array we render
            };
          })
        );


      dbgLike("UI updated (optimistic)", { postId, nowLiked: !alreadyLiked });
    } catch (err) {
      dbgError("like toggle write failed", err, { postId, userUid: user.uid });
      logFirebaseError("community_posts.likeToggle", err, { postId });

      if (isPermissionDenied(err)) {
        toast.error("You don’t have permission to like this post.");
      } else {
        toast.error("Couldn’t update like. Please try again.");
      }
    } finally {
      likeLocks.current.delete(postId);
      dbgLike("END like toggle", { postId });
    }
  };

  // ---------- Comments ----------
  const submitComment = async (postId) => {
    const text = (commentDrafts[postId] || "").trim();
    if (!text) return;

    if (!user) {
      toast.info("Please log in to comment.");
      return;
    }

    try {
      setCommentSending((s) => ({ ...s, [postId]: true }));

      // Create the comment in the subcollection
      const cRef = await addDoc(
        collection(db, `community_posts/${postId}/comments`),
        {
          userId: user.uid,
          text,
          createdAt: serverTimestamp(),
        }
      );

      // Optimistic UI
      const profile = profiles[user.uid] || {};
      const optimistic = {
        id: cRef.id,
        userId: user.uid,
        text,
        createdAt: { toDate: () => new Date() },
        displayName: profile.displayName || "You",
        photoURL: profile.photoURL || "/img/default_profile.png",
      };

      setPosts((prev) =>
        prev.map((p) =>
          p.id === postId
            ? {
                ...p,
                comments: [optimistic, ...(p.comments || [])],
                commentCount:
                  (typeof p.commentCount === "number"
                    ? p.commentCount
                    : p.comments?.length || 0) + 1,
              }
            : p
        )
      );

      setCommentDrafts((d) => ({ ...d, [postId]: "" }));
    } catch (e) {
      logFirebaseError("community_posts.addComment", e, { postId });
      if (isPermissionDenied(e)) {
        toast.error("You don’t have permission to comment.");
      } else {
        toast.error("Couldn’t add comment. Please try again.");
      }
    } finally {
      setCommentSending((s) => ({ ...s, [postId]: false }));
    }
  };

  const formatCount = (n) => {
    if (n < 1000) return n;
    if (n < 1_000_000) return (n / 1000).toFixed(n % 1000 === 0 ? 0 : 1) + "k";
    return (n / 1_000_000).toFixed(n % 1_000_000 === 0 ? 0 : 1) + "M";
  };
  const PAGE_SIZE = 20;

  const CommentsPanel = ({ postId, profiles, commentCount }) => {
    const [expanded, setExpanded] = useState(false);
    const [latest, setLatest] = useState(null); // realtime top comment
    const [page, setPage] = useState([]); // paged older comments (desc)
    const [cursor, setCursor] = useState(null); // last doc for startAfter
    const [loading, setLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true);

    // Realtime latest 1 comment
    useEffect(() => {
      const q = query(
        collection(db, `community_posts/${postId}/comments`),
        orderBy("createdAt", "desc"),
        limit(1)
      );
      const unsub = onSnapshot(q, (snap) => {
        const rows = snap.docs.map((d) => {
          const data = d.data();
          const up = profiles[data.userId] || {};
          return {
            id: d.id,
            ...data,
            displayName: up.displayName || "Anonymous",
            photoURL: up.photoURL || "/img/default_profile.png",
          };
        });
        setLatest(rows[0] || null);
      });
      return () => unsub();
    }, [postId, profiles]);

    // Load a page of older comments (desc)
    const loadPage = async () => {
      if (loading || !hasMore) return;
      setLoading(true);

      let q = query(
        collection(db, `community_posts/${postId}/comments`),
        orderBy("createdAt", "desc"),
        limit(PAGE_SIZE)
      );
      if (cursor) {
        q = query(
          collection(db, `community_posts/${postId}/comments`),
          orderBy("createdAt", "desc"),
          startAfter(cursor),
          limit(PAGE_SIZE)
        );
      }

      const snap = await getDocs(q);
      const rows = snap.docs.map((d) => {
        const data = d.data();
        const up = profiles[data.userId] || {};
        return {
          id: d.id,
          ...data,
          displayName: up.displayName || "Anonymous",
          photoURL: up.photoURL || "/img/default_profile.png",
        };
      });

      const filtered = latest ? rows.filter((r) => r.id !== latest.id) : rows;

      setPage((prev) => [...prev, ...filtered]);
      setCursor(snap.docs[snap.docs.length - 1] || null);
      setHasMore(snap.docs.length === PAGE_SIZE);
      setLoading(false);
    };

    const visibleMoreCount = Math.max(
      (commentCount || 0) - (latest ? 1 : 0),
      0
    );

    return (
      <>
        {!expanded && (
          <>
            {latest ? (
              <ul className="comment-list">
                <li className="comment-item">
                  <img
                    className="comment-avatar"
                    src={latest.photoURL}
                    alt=""
                  />
                  <span className="comment-author">{latest.displayName}</span>
                  <span className="comment-time">
                    {latest.createdAt?.toDate
                      ? latest.createdAt.toDate().toLocaleString(undefined, {
                          month: "short",
                          day: "numeric",
                          hour: "numeric",
                          minute: "2-digit",
                          hour12: true,
                        })
                      : "Just now"}
                  </span>
                  <span className="comment-text">{latest.text || ""}</span>
                </li>
              </ul>
            ) : (
              <div className="no-comments">No comments yet.</div>
            )}

            {visibleMoreCount > 0 && (
              <button
                className="comments-toggle"
                onClick={async () => {
                  setExpanded(true);
                  if (page.length === 0) await loadPage();
                }}
              >
                View {visibleMoreCount} more&nbsp;
                {visibleMoreCount === 1 ? "comment" : "comments"}
              </button>
            )}
          </>
        )}

        {expanded && (
          <>
            <ul className="comment-list">
              {latest && (
                <li className="comment-item">
                  <img
                    className="comment-avatar"
                    src={latest.photoURL}
                    alt=""
                  />
                  <span className="comment-author">{latest.displayName}</span>
                  <span className="comment-time">
                    {latest.createdAt?.toDate
                      ? latest.createdAt.toDate().toLocaleString(undefined, {
                          month: "short",
                          day: "numeric",
                          hour: "numeric",
                          minute: "2-digit",
                          hour12: true,
                        })
                      : "Just now"}
                  </span>
                  <span className="comment-text">{latest.text || ""}</span>
                </li>
              )}

              {page.map((c) => (
                <li key={c.id} className="comment-item">
                  <img className="comment-avatar" src={c.photoURL} alt="" />
                  <span className="comment-author">{c.displayName}</span>
                  <span className="comment-time">
                    {c.createdAt?.toDate
                      ? c.createdAt.toDate().toLocaleString(undefined, {
                          month: "short",
                          day: "numeric",
                          hour: "numeric",
                          minute: "2-digit",
                          hour12: true,
                        })
                      : "Just now"}
                  </span>
                  <span className="comment-text">{c.text || ""}</span>
                </li>
              ))}
            </ul>

            <div className="comment-actions-row">
              {hasMore ? (
                <button
                  className="load-more-btn"
                  disabled={loading}
                  onClick={loadPage}
                >
                  {loading ? "Loading…" : "Load older comments"}
                </button>
              ) : (
                <span className="no-more">No more comments</span>
              )}
              <button
                className="comments-toggle"
                onClick={() => setExpanded(false)}
              >
                Collapse
              </button>
            </div>
          </>
        )}
      </>
    );
  };

  // ---------- Polls (votes subcollection; changeable) ----------
  const PollPanel = ({ post }) => {
    const [voteTally, setVoteTally] = useState({
      total: 0,
      counts: Array.isArray(post.options)
        ? new Array(post.options.length).fill(0)
        : [],
      picked: null, // the current user's selected index
    });

    // Subscribe to /votes for this post and build a tally
    useEffect(() => {
      if (!Array.isArray(post.options)) return () => {};
      const votesRef = collection(db, `community_posts/${post.id}/votes`);
      const unsub = onSnapshot(
        votesRef,
        (snap) => {
          const counts = new Array(post.options.length).fill(0);
          let picked = null;
          snap.forEach((d) => {
            const v = d.data();
            const idx = Number(v?.option);
            if (Number.isInteger(idx) && idx >= 0 && idx < counts.length) {
              counts[idx] += 1;
            }
            if (user?.uid && d.id === user.uid) {
              picked = Number.isInteger(idx) ? idx : null;
            }
          });
          const total = counts.reduce((a, b) => a + b, 0);
          setVoteTally({ total, counts, picked });
        },
        (err) => {
          logFirebaseError("community_posts.votes.onSnapshot", err, {
            postId: post.id,
          });
        }
      );
      return () => unsub();
    }, [db, post.id, post.options?.length, user?.uid]);

    const computePercents = (counts) => {
      const total = counts.reduce((a, b) => a + b, 0);
      const percents = counts.map((v) => {
        if (total === 0) return 0;
        const pct = (v / total) * 100;
        return Number.isInteger(pct) ? pct : Number(pct.toFixed(1));
      });
      return { total, percents };
    };

    const { total, percents } = computePercents(voteTally.counts);

    const submitPollVote = async (idx) => {
      if (!user) {
        toast.info("Please log in to vote.");
        return;
      }
      if (!Array.isArray(post.options) || idx == null) {
        toast.info("Please pick an option.");
        return;
      }
      setPollSubmitting((s) => ({ ...s, [post.id]: true }));
      try {
        const vRef = doc(db, `community_posts/${post.id}/votes/${user.uid}`);
        if (voteTally.picked == null) {
          // First vote -> create
          await setDoc(vRef, {
            userId: user.uid,
            option: idx,
            createdAt: serverTimestamp(),
          });
        } else {
          // Change vote -> update
          await updateDoc(vRef, {
            option: idx,
            updatedAt: serverTimestamp(),
          });
        }
      } catch (e) {
        logFirebaseError("community_posts.voteWrite", e, {
          postId: post.id,
          idx,
        });
        if (isPermissionDenied(e)) {
          toast.error("Voting is blocked by Firestore rules.");
        } else {
          toast.error("Couldn’t submit your vote. Please try again.");
        }
      } finally {
        setPollSubmitting((s) => ({ ...s, [post.id]: false }));
      }
    };

    const hasVoted = Number.isInteger(voteTally.picked);

    return (
      <div className="poll-block">
        {hasVoted ? (
          // RESULTS (after vote) — rows clickable to change vote
          <>
            <div className="poll-results" aria-live="polite">
              {post.options.map((opt, idx) => {
                const votes = voteTally.counts[idx] || 0;
                const isPicked = voteTally.picked === idx;
                return (
                  <button
                    key={idx}
                    type="button"
                    className={`poll-result-row ${isPicked ? "picked" : ""}`}
                    onClick={() => {
                      if (!user || pollSubmitting[post.id]) return;
                      submitPollVote(idx);
                    }}
                    disabled={!user || pollSubmitting[post.id]}
                    aria-label={`${opt.text}: ${percents[idx]}% (${votes} votes) — click to change your vote`}
                  >
                    <div className="result-top">
                      <div className="result-label">
                        <span className="result-text">{opt.text}</span>
                        {isPicked && (
                          <span className="your-vote-chip">Your vote</span>
                        )}
                      </div>
                      <div className="result-value">
                        {percents[idx]}% · {votes} votes
                      </div>
                    </div>

                    <div className="result-track" aria-hidden="true">
                      <div
                        className="result-fill"
                        style={{ width: `${percents[idx]}%` }}
                      />
                    </div>
                  </button>
                );
              })}
            </div>

            <div className="poll-footer">
              <span className="poll-total">Total votes: {total}</span>
              {!user ? (
                <span className="poll-locked">Log in to participate</span>
              ) : (
                <span className="poll-locked">
                  Tap a row to change your vote
                </span>
              )}
            </div>
          </>
        ) : (
          // PRE-VOTE (user has NOT chosen yet)
          <>
            <div className="poll-header-row">
              <span className="poll-title">Cast your vote</span>
              {!user && (
                <span className="poll-badge lock" aria-hidden="true">
                  🔒
                </span>
              )}
            </div>

            <div
              className="poll-choices"
              role="radiogroup"
              aria-label="Choose one option"
            >
              {post.options.map((opt, idx) => (
                <label
                  key={idx}
                  className={`poll-choice-card ${
                    !user || pollSubmitting[post.id] ? "disabled" : ""
                  }`}
                  tabIndex={!user || pollSubmitting[post.id] ? -1 : 0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      if (!user || pollSubmitting[post.id]) return;
                      submitPollVote(idx);
                    }
                  }}
                >
                  {/* keep native radio for a11y, just hidden */}
                  <input
                    type="radio"
                    name={`poll-${post.id}`}
                    onChange={() => submitPollVote(idx)}
                    disabled={!user || pollSubmitting[post.id]}
                    aria-label={opt.text}
                  />
                  <span className="choice-bullet" aria-hidden="true" />
                  <span className="choice-text">{opt.text}</span>
                  <span className="choice-cta" aria-hidden="true">
                    {pollSubmitting[post.id] ? "Submitting…" : "Tap to vote"}
                  </span>
                </label>
              ))}
            </div>

            {!user ? (
              <div className="poll-login-wall" role="note">
                <div className="wall-text">
                  Log in to vote and see results instantly.
                </div>
              </div>
            ) : (
              <div className="poll-hint" role="note">
                One vote per user · You can change it anytime
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  return (
    <>
      <div className="forum-container">
        <div className="forum-toolbar">
          <div className="forum-tabs">
            {["all", "blip", "question", "poll"]
              .concat(user ? ["mine"] : [])
              .map((t) => (
                <span
                  key={t}
                  className={`tab-text ${filter === t ? "active" : ""}`}
                  onClick={() => setFilter(t)}
                >
                  {t === "all"
                    ? "All"
                    : t === "blip"
                    ? "Blips"
                    : t === "question"
                    ? "Questions"
                    : t === "poll"
                    ? "Polls"
                    : "My Posts"}
                </span>
              ))}
          </div>

          <div className="toolbar-right">
            <input
              className="forum-search"
              placeholder="Search topics or users"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Search topics or users"
            />

            <button
              className="create-post-btn"
              onClick={() => {
                if (!user) {
                  toast.info("Please log in to create a post.");
                  return;
                }
                setShowModal(true);
              }}
            >
              ＋ Create Post
            </button>
          </div>

          <button
            className="create-post-fab"
            onClick={() => {
              if (!user) {
                toast.info("Please log in to create a post.");
                return;
              }
              setShowModal(true);
            }}
            aria-label="Create Post"
            title="Create Post"
          >
            ＋
          </button>
        </div>

        <ul className="forum-post-list">
          {filteredPosts.map((post) => (
            <li key={post.id} className="forum-post">
              <div className="forum-post-header-row">
                <img
                  className="avatar-img"
                  src={
                    userProfiles[post.userId]?.photoURL ||
                    "/img/default_profile.png"
                  }
                  alt="avatar"
                />
                <div className="post-content">
                  <ReactMarkdown>{post.content}</ReactMarkdown>
                  {post.image && (
                    <div className="forum-post-image">
                      <img
                        src={post.image}
                        alt="Post Attachment"
                        onClick={() => setFullscreenImage(post.image)}
                        style={{ cursor: "pointer" }}
                      />
                    </div>
                  )}
                  <span className="post-meta">
                    {userProfiles[post.userId]?.displayName || "Unknown User"} •{" "}
                    {post.type.charAt(0).toUpperCase() + post.type.slice(1)} •{" "}
                    {(() => {
                      const d = toDateSafe(post.timestamp);
                      return d
                        ? d.toLocaleString(undefined, {
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                            hour: "numeric",
                            minute: "2-digit",
                            hour12: true,
                          })
                        : "Just now";
                    })()}
                  </span>
                </div>
              </div>

              {post.type === "poll" && Array.isArray(post.options) && (
                <PollPanel post={post} />
              )}

              <div className="forum-post-footer">
                <div className="post-stats">
                  {post.type !== "blip" && post.type !== "poll" && (
                    <>
                      💬{" "}
                      {formatCount(
                        typeof post.commentCount === "number"
                          ? post.commentCount
                          : post.comments?.length || 0
                      )}{" "}
                    </>
                  )}
                  <button
                    className={`like-button ${
                      post.likes?.includes(user?.uid) ? "liked" : ""
                    }`}
                    onClick={() => handleLikePost(post.id)}
                    title={!user ? "Log in to like posts" : ""}
                    disabled={likeLocks.current.has(post.id)}
                  >
                    <span className="heart-icon">
                      {post.likes?.includes(user?.uid) ? "❤️" : "🤍"}
                    </span>{" "}
                    {post.likes?.length || 0}
                  </button>
                </div>
              </div>

              {post.type === "question" && (
                <div className="forum-comment-section">
                  <CommentsPanel
                    postId={post.id}
                    profiles={profiles}
                    commentCount={post.commentCount}
                  />

                  <div className="forum-comment-form">
                    <input
                      className="forum-comment-input"
                      placeholder={
                        user ? "Write a comment…" : "Log in to comment"
                      }
                      value={commentDrafts[post.id] || ""}
                      disabled={!user || commentSending[post.id]}
                      onChange={(e) =>
                        setCommentDrafts((d) => ({
                          ...d,
                          [post.id]: e.target.value,
                        }))
                      }
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          submitComment(post.id);
                        }
                      }}
                    />
                    <button
                      className="comment-send-btn"
                      aria-label="Send comment"
                      title="Send"
                      disabled={
                        !user ||
                        commentSending[post.id] ||
                        !(commentDrafts[post.id] || "").trim()
                      }
                      onClick={() => submitComment(post.id)}
                    >
                      ➤
                    </button>
                  </div>

                  {!user && (
                    <div className="comment-login-message">
                      Please log in to add a comment.
                    </div>
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>
      </div>

      {showModal && (
        <CreatePostModal
          user={user}
          onClose={() => setShowModal(false)}
          onPostCreated={handlePostCreated}
        />
      )}
      {fullscreenImage && (
        <FullscreenImageModal
          imageUrl={fullscreenImage}
          onClose={() => setFullscreenImage(null)}
        />
      )}
    </>
  );
};

export default GeneralDiscussion;
